import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  ReportPreviewContentSection,
  ReportPreviewPerformanceSection,
  ReportPreviewProfile,
  ReportPreviewReport,
} from 'api/analytics-reports/types';
import { getMetricDisplay } from 'app/modules/analytics/modules/performance/utils/getPerformanceMetricDisplay';
import { getReportContentMetricValue } from 'app/modules/analytics/modules/reports/modules/createReport/utils/getReportContentMetricValue';
import ReportProfilePreviewLayout from 'app/modules/analytics/modules/reports/layouts/ReportProfilePreviewLayout';
import { buildPlatformReportPreviewPages } from 'app/modules/analytics/modules/reports/utils/buildPlatformReportPreviewPages';
import { getReportPreviewSectionNoteVisibility } from 'app/modules/analytics/modules/reports/utils/getReportPreviewSectionNoteVisibility';
import { splitPerformanceMetricsByChartSupport } from 'app/modules/analytics/modules/reports/utils/getPerformanceChartConfig';
import { resolvePreviewContentPosts } from 'app/modules/analytics/modules/reports/utils/resolvePreviewContentPosts';
import { isReportContentPostsEnabled } from 'app/modules/analytics/modules/reports/constants/reportContentPostSettings';
import { resolvePreviewContentSummary } from 'app/modules/analytics/modules/reports/utils/resolvePreviewContentSummary';
import {
  isPreviewPerformanceDataMissing,
  resolvePreviewPerformanceOverview,
} from 'app/modules/analytics/modules/reports/utils/resolvePreviewPerformanceOverview';
import ViewReportPreviewPageShell from './ViewReportPreviewPageShell';
import type { ReportPreviewLayout } from 'app/modules/analytics/modules/reports/constants/reportPreviewPageShellClassName';
import { resolveReportPreviewProfileHeaderMeta } from 'app/modules/analytics/modules/reports/utils/resolveReportPreviewProfileHeaderMeta';
import { resolveReportPreviewOverviewSection } from 'app/modules/analytics/modules/reports/utils/resolveReportPreviewOverviewSection';
import ReportPreviewOverviewPage from 'app/modules/analytics/modules/reports/components/reportPreview/overview/ReportPreviewOverviewPage';

interface Props {
  profile: ReportPreviewProfile;
  report: ReportPreviewReport;
  startPageNumber: number;
  totalPages: number;
  layout?: ReportPreviewLayout;
  breakBeforeFirstPage?: boolean;
}

const ViewReportPreviewPerformanceProfileCard = ({
  profile,
  report,
  startPageNumber,
  totalPages,
  layout = 'modal',
  breakBeforeFirstPage = false,
}: Props) => {
  const { t } = useTranslation();

  const performanceSection = profile.sections.find(
    (section): section is ReportPreviewPerformanceSection =>
      section.kind === 'performance',
  );
  const contentSection = profile.sections.find(
    (section): section is ReportPreviewContentSection =>
      section.kind === 'content',
  );

  const performanceOverview = useMemo(
    () =>
      resolvePreviewPerformanceOverview(profile.type, performanceSection?.data),
    [performanceSection?.data, profile.type],
  );

  const isPerformanceDataMissing = isPreviewPerformanceDataMissing(
    profile.type,
    performanceSection?.data,
  );

  const performancePages = performanceSection?.data?.pages ?? [];
  const contentSummary = resolvePreviewContentSummary(contentSection?.data);
  const contentPosts = resolvePreviewContentPosts(contentSection?.data);
  const includePosts =
    contentSection?.kind === 'content' &&
    isReportContentPostsEnabled({
      content_post_count: contentSection.post_count,
    });

  const { chartMetrics, tileMetrics } = useMemo(
    () =>
      splitPerformanceMetricsByChartSupport(performanceSection?.metrics ?? []),
    [performanceSection?.metrics],
  );

  const profileHeaderMeta = resolveReportPreviewProfileHeaderMeta(profile, t);

  const previewPages = useMemo(
    () =>
      buildPlatformReportPreviewPages({
        pageType: profileHeaderMeta.pageType,
        cardType: profile.type,
        chartMetrics,
        performanceTileMetrics: tileMetrics,
        performanceMetrics: performanceSection?.metrics ?? [],
        contentMetrics: contentSection?.metrics ?? [],
        posts: contentPosts,
        includePosts,
        performanceNote: performanceSection?.note,
        contentNote: contentSection?.note,
      }),
    [
      chartMetrics,
      tileMetrics,
      contentPosts,
      contentSection?.metrics,
      contentSection?.note,
      includePosts,
      performanceSection?.metrics,
      performanceSection?.note,
      profile.type,
      profileHeaderMeta.pageType,
    ],
  );

  const getPerformanceMetricValue = useCallback(
    (metricKey: string) =>
      getMetricDisplay(performanceOverview, metricKey).value,
    [performanceOverview],
  );

  const getContentMetricValue = useCallback(
    (metricKey: string) =>
      getReportContentMetricValue(contentSummary, metricKey) ?? '—',
    [contentSummary],
  );

  const since = report.period.from ?? '';
  const until = report.period.to ?? '';

  if (previewPages.length === 0) {
    return null;
  }

  return (
    <>
      {previewPages.map((page, index) => {
        const { showPerformanceNote, showContentNote } =
          getReportPreviewSectionNoteVisibility(previewPages, index, {
            performanceNote: performanceSection?.note,
            contentNote: contentSection?.note,
          });

        return (
          <ViewReportPreviewPageShell
            key={`${profile.id}-page-${index}`}
            profileLabel={profileHeaderMeta.label}
            profileLogoSrc={profileHeaderMeta.logoSrc}
            profilePageType={profileHeaderMeta.pageType}
            reportName={report.name}
            since={since}
            until={until}
            pageNumber={startPageNumber + index}
            totalPages={totalPages}
            layout={layout}
            breakBeforeFirstPage={breakBeforeFirstPage}
          >
            <ReportProfilePreviewLayout
              page={page}
              cardType={profile.type}
              pageType={profileHeaderMeta.pageType}
              performanceMetrics={performanceSection?.metrics ?? []}
              contentMetrics={contentSection?.metrics ?? []}
              performancePages={performancePages}
              performanceOverview={performanceOverview}
              isPerformanceLoading={false}
              getPerformanceMetricValue={getPerformanceMetricValue}
              performanceEmptyMessage={
                isPerformanceDataMissing
                  ? t('analyticsPerformance.noDataAvailable')
                  : undefined
              }
              isContentLoading={false}
              isContentPostsLoading={false}
              getContentMetricValue={getContentMetricValue}
              contentPosts={contentPosts}
              performanceNote={performanceSection?.note}
              contentNote={contentSection?.note}
              showPerformanceNote={showPerformanceNote}
              showContentNote={showContentNote}
            />
          </ViewReportPreviewPageShell>
        );
      })}
    </>
  );
};

const ViewReportPreviewProfileCard = (props: Props) => {
  const overviewSection = resolveReportPreviewOverviewSection(props.profile);

  // TODO(overview-pdf-be): Overview PDF layout sa aktivuje až keď preview vráti kind: 'overview'.
  // @see overviewPdfExportBackendGaps.ts
  if (overviewSection) {
    return (
      <ReportPreviewOverviewPage
        profile={props.profile}
        report={props.report}
        overviewSection={overviewSection}
        startPageNumber={props.startPageNumber}
        totalPages={props.totalPages}
        layout={props.layout}
        breakBeforeFirstPage={props.breakBeforeFirstPage}
      />
    );
  }

  return <ViewReportPreviewPerformanceProfileCard {...props} />;
};

export default ViewReportPreviewProfileCard;
